import { ClientAttributes } from "./getClientAttributes";
import { formatTranscriptPunctuation } from "./formatTranscriptPunctuation";
import { CaptionFrame } from "./types";

type TranscriptConfig = Pick<ClientAttributes, "useUppercase"> & {
  insertParagraphs: boolean;
  delaySeconds?: number;
  language?: string;
};

export class Transcript {
  /**
   * The caption frames to display
   */
  private displayedFrames: CaptionFrame[] = [];

  /**
   * Whether or not the transcript should include character count based paragraphs
   */
  private insertParagraphs: TranscriptConfig["insertParagraphs"];

  /**
   * The minimum number of characters that should be in a paragraph. When the number
   * is reached, then the transcript should be split on the next period.
   */
  private MIN_CHARS_IN_PARAGRAPH = 200;

  /**
   * The transcript broken into paragraphs based off the MIN_CHARS_IN_PARAGRAPH
   *
   * This is saved to avoid needing to re-split this for each new caption response
   */
  private transcriptParagraphs: string[] = [];

  /**
   * Whether or not the captions should be displayed in uppercase
   */
  private useUppercase: TranscriptConfig["useUppercase"];

  /**
   * Number of seconds to delay displaying captions after they are produced.
   */
  private delaySeconds: TranscriptConfig["delaySeconds"];

  /**
   * The caption language (browser_navigator_code), used for locale-aware casing.
   */
  private language: TranscriptConfig["language"];

  constructor({
    insertParagraphs,
    useUppercase,
    delaySeconds,
    language,
  }: TranscriptConfig) {
    this.insertParagraphs = insertParagraphs;
    this.useUppercase = useUppercase;
    this.delaySeconds = delaySeconds;
    this.language = language;
  }

  /**
   * Whether any frames have been ingested (regardless of delay eligibility)
   */
  hasFrames(): boolean {
    return this.displayedFrames.length > 0;
  }

  /**
   * Add frames to the transcript
   */
  addFrames(frames: CaptionFrame[]) {
    /**
     * The new frames with content to display
     */
    const newDisplayFrames = frames.filter((frame) => {
      const [lastDisplayedFrame] = this.displayedFrames.slice(-1);
      return (
        frame.packet_number != null &&
        frame.transcript &&
        (lastDisplayedFrame
          ? frame.packet_number > lastDisplayedFrame.packet_number
          : true)
      );
    });

    // Update the displayed frames state
    this.displayedFrames = [...this.displayedFrames, ...newDisplayFrames];
  }

  /**
   * Return an HTML representation of the transcript
   */
  getHtml() {
    return this.createHtml(
      this.transformTranscript(
        formatTranscriptPunctuation(this.getEligibleFrames())
      )
    );
  }

  /**
   * Filter frames based on delay eligibility.
   * A frame is eligible if no delay is configured, if sent_to_target_at is missing,
   * or if sent_to_target_at + delaySeconds <= now.
   */
  private getEligibleFrames(): CaptionFrame[] {
    if (!this.delaySeconds) {
      return this.displayedFrames;
    }

    const now = Date.now();
    const delayMs = this.delaySeconds * 1000;

    return this.displayedFrames.filter((frame) => {
      const sentAt = frame.connector_caption_output?.sent_to_target_at;
      if (!sentAt) return true;
      return new Date(sentAt).getTime() + delayMs <= now;
    });
  }

  /**
   * Transform the transcript based off config.
   *
   * Uppercasing is locale-aware when a language is known so scripts with
   * language-specific casing rules (e.g. Turkish i) render correctly; it is a
   * no-op on caseless scripts (e.g. CJK). Without a language it falls back to
   * the default locale uppercasing, matching the source-only behavior.
   */
  private transformTranscript(transcript: string) {
    if (!this.useUppercase) {
      return transcript;
    }

    return this.language
      ? transcript.toLocaleUpperCase(this.language)
      : transcript.toUpperCase();
  }

  /**
   * Turn the transcript into sanitized HTML split into paragraphs based off
   * the MIN_CHARS_IN_PARAGRAPH config
   *
   * This function is adapted from 3players
   *
   * WARNING: Be careful not to introduce XSS vulnerabilities, as we are skipping
   * outside Preact framework that helps you avoid these by default.
   */
  private createHtml(transcript: string) {
    /**
     * A single HTML element for sanitizing the transcript content
     *
     * Since we don't have a Sanitization API stable in browsers yet, we'll use
     * an element to take advantage of built-in sanitization with innerText/innerHTML.
     *
     * We reuse a single element for each word to keep things cheap.
     */
    const sanitizationElement = document.createElement("div");

    /** The length of the rendered transcript */
    const renderedTranscriptLength = this.transcriptParagraphs.join("").length;

    /** The content of the last paragraph */
    let nextParagraphContent = this.transcriptParagraphs.pop() || "";

    /**
     * Regex for matching end of sentence characters, including the Arabic
     * question mark (U+061F), which is distinct from "?"
     */
    const endOfSentenceRegex = /[.?!؟]/;

    /** Regex for matching speaker labels */
    const speakerLabelRegex = /[[]/;

    // Loop through each character to set paragraph breakpoints.
    // If the complete transcript is being displayed, set these after an end of
    // sentence character and before a speaker label.
    // If the complete transcript is NOT being displayed, simply set these before
    // speaker labels.
    for (
      let index = renderedTranscriptLength;
      index < transcript.length;
      index += 1
    ) {
      nextParagraphContent += transcript[index];
      if (
        (this.insertParagraphs &&
          nextParagraphContent.length > this.MIN_CHARS_IN_PARAGRAPH &&
          endOfSentenceRegex.test(transcript[index])) ||
        speakerLabelRegex.test(transcript[index + 1])
      ) {
        this.transcriptParagraphs.push(nextParagraphContent);
        nextParagraphContent = "";
      }
    }

    // Handle remaining text
    if (nextParagraphContent !== "")
      this.transcriptParagraphs.push(nextParagraphContent);

    // Sanitize the transcript content and add paragraph tags
    return this.transcriptParagraphs
      .map((content) => {
        sanitizationElement.innerText = content.trim();
        const sanitizedText = sanitizationElement.innerHTML;
        return `<p>${sanitizedText}</p>`;
      })
      .join("");
  }
}
